import { addMonths } from 'date-fns';
import { dateFromSerial, isDateFormat, parseDate } from 'numfmt';
import { IExtractedField } from 'types/db/ExtractedProForma';
import { Row, Section } from 'views/ProFormaTable/components/ExecutiveSummary/ExecutiveSummaryGenericTable/types';
import {
    buildFieldMap, displayValue, FieldMap, getField, rowId,
} from 'views/ProFormaTable/components/ExecutiveSummary/resolvers/extracted/cells';
import { TimelineData } from 'views/ProFormaTable/types';
import { TIMELINE_KEYS } from './constants';

const rowsForKey = (fieldMap: FieldMap, key: string): Row[] => (fieldMap.get(key) ?? []).map((field, index) => ({
    id: `${rowId(field.key)}-${index}`,
    label: field.label,
    values: [{ value: displayValue(field), format: 'none' as const }],
}));

export const extractedAddress = (fields: IExtractedField[]): string | undefined => {
    const addressField = fields.find(field => field.key === 'details.address');
    if (!addressField || addressField.cannotLocate || !addressField.value.trim()) return undefined;
    return addressField.value;
};

// ---------------------------------------------------------------------------
// Timeline
//
// The pro forma page draws the timeline as a chart, so the extracted page does too. A workbook
// states each entry either as a date or as a month counted from the start of the analysis
// (`pro_forma_executive_summary_fields.md`), and the chart needs a date for every point, so an
// offset is resolved against whatever the analysis start turned out to be.
// ---------------------------------------------------------------------------

const MAX_MONTH_OFFSET = 1200;

type TimelineEntry =
    | { kind: 'date'; date: Date }
    | { kind: 'monthOffset'; months: number };

const dateFromExcelSerial = (serial: number): Date => {
    const [year, month, day] = dateFromSerial(serial);
    return new Date(year, month - 1, day);
};

const isoDate = (date: Date): string => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
].join('-');

const readTimelineCell = (field: IExtractedField | undefined): TimelineEntry | undefined => {
    const value = field?.value.trim();
    if (!field || field.cannotLocate || !value) return undefined;

    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
        const parsed = parseDate(value);
        return typeof parsed?.v === 'number' ? { kind: 'date', date: dateFromExcelSerial(parsed.v) } : undefined;
    }

    if (isDateFormat(field.numberFormat) || numeric > MAX_MONTH_OFFSET) {
        return { kind: 'date', date: dateFromExcelSerial(numeric) };
    }

    return { kind: 'monthOffset', months: numeric };
};

/**
 * The extracted timeline in the shape `ExecutiveSummaryTimelineChart`
 * or null if analysis start is itself a month number so no offset for it can be resolved (showing a table instead)
 */
export const buildTimelineData = (fields: IExtractedField[], dealName: string): TimelineData | null => {
    const fieldMap = buildFieldMap(fields);
    const analysisStart = readTimelineCell(getField(fieldMap, 'timeline.analysis_start'));
    const anchor = analysisStart?.kind === 'date' ? analysisStart.date : undefined;

    const dateFor = (key: string): string | undefined => {
        const entry = readTimelineCell(getField(fieldMap, key));
        if (!entry) return undefined;
        if (entry.kind === 'date') return isoDate(entry.date);
        return anchor ? isoDate(addMonths(anchor, entry.months)) : undefined;
    };

    const dates = {
        analysisStart: dateFor('timeline.analysis_start'),
        firstUnitDelivered: dateFor('timeline.first_unit_delivered'),
        developmentStartDate: dateFor('timeline.development_start_date'),
        developmentEndDate: dateFor('timeline.development_end_date'),
        operationStartDate: dateFor('timeline.operation_start_date'),
        firstStabilizedMonth: dateFor('timeline.first_stabilized_month'),
        saleMonth: dateFor('timeline.sale_month'),
    };

    return Object.values(dates).some(Boolean) ? { ...dates, dealName } : null;
};

/**
 * The timeline as a table, for when an Excel has analysis as a month number so no beginning offset
 * can be calculated for the timeline
 */
export const buildTimelineSections = (fields: IExtractedField[]): Section[] => {
    const fieldMap = buildFieldMap(fields);
    const rows = TIMELINE_KEYS.flatMap(key => rowsForKey(fieldMap, key));
    return rows.length > 0 ? [{ id: 'timeline-section', columns: ['Value'], rows }] : [];
};
